// ExcelImport Component - v2.0 (Socket.IO Fixed)
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Alert, AlertDescription } from '@/Components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/Components/ui/accordion.jsx';
import { Progress } from '@/Components/ui/progress';
import { 
  Download, 
  Upload, 
  FileSpreadsheet, 
  CheckCircle, 
  AlertCircle,
  Info,
  Loader2,
  HelpCircle
} from 'lucide-react';
import axios from 'axios';
import { io } from 'socket.io-client';
import ImportProgressModal from './ImportProgressModal';
import logger from '../../Entities/LoggerService';

const ExcelImport = ({ onImportSuccess }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [socketEvents, setSocketEvents] = useState(null);
  const [socket, setSocket] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get('/api/partnerships/template/excel', {
        responseType: 'blob'
      });
      
      // Criar URL do blob e fazer download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_parcerias_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      logger.error('import', 'ExcelImport.handleDownloadTemplate', 'Erro ao baixar template', {}, error);
      setError('Erro ao baixar template Excel');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      
      if (!validTypes.includes(file.type)) {
        setError('Apenas arquivos Excel (.xlsx, .xls) são permitidos');
        return;
      }
      function formatBytes(bytes, decimals = 2) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
      }
      // Validar tamanho (10MB)
      if (file.size > 10485760) {
        setError('Arquivo muito grande. Máximo permitido: ' + formatBytes(10485760));
        return;
      }
      
      setSelectedFile(file);
      setError('');
      setImportResult(null);
    }
  };

  // Configurar Socket.IO
  useEffect(() => {
    // ✅ Em produção: usa mesma URL do frontend (Nginx faz proxy)
    // ✅ Em desenvolvimento: usa porta 3010 diretamente
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const serverUrl = isDevelopment 
      ? `${window.location.protocol}//${window.location.hostname}:3010`
      : window.location.origin; // Usa mesma origem (Nginx faz proxy)
    
    const newSocket = io(serverUrl, {
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 10,
      timeout: 20000,
      transports: ['websocket', 'polling'],
      path: '/socket.io' // ✅ Caminho do Socket.IO (Nginx faz proxy)
    });

    newSocket.on('connect', () => {
      logger.info('import', 'ExcelImport.socketConnect', 'Socket conectado', {
        socketId: newSocket.id
      });
    });

    newSocket.on('import_progress', (data) => {
      // ✅ OTIMIZAÇÃO: Removido logger.debug para evitar 429 (Too Many Requests)
      // Durante importação de 467 parcerias, isso causava 467+ requisições ao backend
      // O progresso já é exibido no modal, não precisa logar no backend
      setSocketEvents(data);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Selecione um arquivo Excel primeiro');
      return;
    }

    if (!socket || !socket.connected) {
      setError('Conexão com servidor não estabelecida. Tente novamente.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError('');
    setImportResult(null);
    setShowProgressModal(true); // Abrir modal
    setSocketEvents(null);

    try {
      const formData = new FormData();
      formData.append('excel', selectedFile);
      /*
      console.log('📤 Enviando arquivo:', {
        nome: selectedFile.name,
        tamanho: selectedFile.size,
        tipo: selectedFile.type
      });
      */
      // Enviar socket ID para o servidor
      const response = await axios.post(
        `/api/partnerships/import/excel?socketId=${socket.id}`, 
        formData, 
        {
          headers: {
            // Não definir Content-Type manualmente - deixar o navegador definir com boundary
            // 'Content-Type': 'multipart/form-data' 
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      setImportResult(response.data);
      
      if (response.data.success && onImportSuccess) {
        onImportSuccess(response.data);
      }

    } catch (error) {
      logger.error('import', 'ExcelImport.handleUpload', 'Erro no upload', {
        fileName: selectedFile?.name,
        fileSize: selectedFile?.size,
        errorResponse: error.response?.data
      }, error);
      
      if (error.response?.data?.error) {
        setError(error.response.data.error);
        
        if (error.response.data.summary) {
          setImportResult({
            success: false,
            summary: error.response.data.summary,
            processingErrors: error.response.data.processingErrors
          });
        }
      } else {
        setError('Erro ao fazer upload do arquivo');
      }
      
      // NÃO fechar modal - deixar usuário ver o erro
      // Emitir evento de erro para o modal
      setSocketEvents({
        event: 'error',
        data: {
          message: error.response?.data?.error || 'Erro ao processar arquivo Excel'
        }
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setImportResult(null);
    setError('');
    setUploadProgress(0);
    // Limpar input file
    const fileInput = document.getElementById('excel-file-input');
    if (fileInput) fileInput.value = '';
  };

  return (
    <>
      {/* Modal de Progresso */}
      <ImportProgressModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        importData={importResult}
        socketEvents={socketEvents}
      />

      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Importação via Excel
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
        
        {/* Accordion de Instruções */}
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-0">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="instructions" className="border-none">
                <AccordionTrigger className="hover:no-underline px-4 py-4">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-blue-600" />
                    <h3 className="font-bold text-blue-900 text-lg">Instruções Importantes</h3>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <Accordion type="single" collapsible className="w-full space-y-3">
                    
                    {/* Sub-Accordion 1: Como usar o template */}
                    <AccordionItem value="inst-sub-1" className="bg-white rounded-lg border border-blue-200">
                      <AccordionTrigger className="hover:no-underline px-4 py-3">
                        <div className="flex items-center gap-2 text-left">
                          <FileSpreadsheet className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <span className="font-semibold text-blue-900 text-sm">Como Usar o Template</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-3">
                          <p className="text-sm text-blue-700">Siga estas etapas para importar suas parcerias</p>
                          <ul className="text-sm text-blue-800 space-y-2 list-disc list-inside">
                            <li>Baixe o template Excel clicando no botão abaixo</li>
                            <li>Preencha com os dados das parcerias (uma por linha)</li>
                            <li><strong>Não altere os nomes das colunas</strong> do cabeçalho</li>
                            <li>Salve o arquivo em formato Excel (.xlsx)</li>
                            <li>Faça o upload do arquivo preenchido</li>
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Sub-Accordion 2: Campos obrigatórios */}
                    <AccordionItem value="inst-sub-2" className="bg-white rounded-lg border border-red-200">
                      <AccordionTrigger className="hover:no-underline px-4 py-3">
                        <div className="flex items-center gap-2 text-left">
                          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                          <span className="font-semibold text-red-900 text-sm">Campos Obrigatórios</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-3">
                          <p className="text-sm text-red-700">Estes campos devem ser preenchidos obrigatoriamente</p>
                          <ul className="text-sm text-red-800 space-y-2 list-disc list-inside">
                            <li><strong>Ano:</strong> Ano da parceria (ex: 2024)</li>
                            <li><strong>CNPJ:</strong> CNPJ da organização (com ou sem formatação)</li>
                            <li><strong>OSC:</strong> Nome completo da Organização da Sociedade Civil</li>
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Sub-Accordion 3: Formatos aceitos */}
                    <AccordionItem value="inst-sub-3" className="bg-white rounded-lg border border-green-200">
                      <AccordionTrigger className="hover:no-underline px-4 py-3">
                        <div className="flex items-center gap-2 text-left">
                          <Info className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="font-semibold text-green-900 text-sm">Formatos Aceitos</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-3">
                          <p className="text-sm text-green-700">Como preencher cada tipo de campo corretamente</p>
                          <div className="space-y-3">
                            <div className="bg-green-50 p-3 rounded border border-green-100">
                              <p className="text-sm font-semibold text-green-900">📅 Datas:</p>
                              <p className="text-sm text-green-800 mt-1">Use o formato brasileiro <strong>DD/MM/AAAA</strong></p>
                              <p className="text-xs text-green-700 mt-1">Exemplo: 15/01/2024</p>
                            </div>
                            <div className="bg-green-50 p-3 rounded border border-green-100">
                              <p className="text-sm font-semibold text-green-900">🏢 CNPJ:</p>
                              <p className="text-sm text-green-800 mt-1">Com ou sem formatação</p>
                              <p className="text-xs text-green-700 mt-1">Exemplos: 12.345.678/0001-90 ou 12345678000190</p>
                            </div>
                            <div className="bg-green-50 p-3 rounded border border-green-100">
                              <p className="text-sm font-semibold text-green-900">💰 Valores:</p>
                              <p className="text-sm text-green-800 mt-1">Use vírgula ou ponto para decimais</p>
                              <p className="text-xs text-green-700 mt-1">Exemplos: 1.000,50 ou 1000.50</p>
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Sub-Accordion 4: Limites e restrições */}
                    <AccordionItem value="inst-sub-4" className="bg-white rounded-lg border border-amber-200">
                      <AccordionTrigger className="hover:no-underline px-4 py-3">
                        <div className="flex items-center gap-2 text-left">
                          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                          <span className="font-semibold text-amber-900 text-sm">Limites e Restrições</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-3">
                          <p className="text-sm text-amber-700">Atenção a estes limites técnicos</p>
                          <ul className="text-sm text-amber-800 space-y-2 list-disc list-inside">
                            <li><strong>Tamanho máximo:</strong> 10 MB por arquivo</li>
                            <li><strong>Formato:</strong> Apenas arquivos .xlsx (Excel)</li>
                            <li><strong>Duplicatas:</strong> Sistema detecta e alerta sobre CNPJs duplicados</li>
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                  </Accordion>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Seção 1: Download do Template */}
        <div className="space-y-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Download className="w-4 h-4" />
            1. Baixar Template
          </h3>
          <p className="text-sm text-gray-600">
            Baixe o template Excel com as colunas e instruções necessárias.
          </p>
          <Button 
            onClick={handleDownloadTemplate}
            variant="outline"
            className="w-full sm:w-auto"
          >
            <Download className="w-4 h-4 mr-2" />
            Baixar Template Excel
          </Button>
        </div>

        <hr />

        {/* Seção 2: Upload do Arquivo */}
        <div className="space-y-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Upload className="w-4 h-4" />
            2. Fazer Upload do Arquivo Preenchido
          </h3>
          
          <div className="space-y-3">
            <input
              id="excel-file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            
            {selectedFile && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                Arquivo selecionado: {selectedFile.name}
              </div>
            )}
            
            <div className="flex gap-2">
              <Button 
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                className="flex-1 sm:flex-none"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importar Parcerias
                  </>
                )}
              </Button>
              
              {(selectedFile || importResult) && (
                <Button 
                  onClick={resetUpload}
                  variant="outline"
                  disabled={isUploading}
                >
                  Limpar
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        {isUploading && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Enviando arquivo...</span>
              <span>{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="w-full" />
          </div>
        )}

        {/* Resultado da Importação */}
        {importResult && (
          <div className="space-y-3">
            {importResult.success ? (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>{importResult.message}</strong>
                  {importResult.summary && (
                    <div className="mt-2 text-sm">
                      <div>Total de linhas: {importResult.summary.totalRows}</div>
                      <div>Parcerias criadas: {importResult.summary.createdRows}</div>
                      {importResult.summary.errorRows > 0 && (
                        <div className="text-orange-600">
                          Linhas com erro: {importResult.summary.errorRows}
                        </div>
                      )}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  <strong>Falha na importação</strong>
                  {importResult.summary && (
                    <div className="mt-2 text-sm">
                      <div>Total de linhas: {importResult.summary.totalRows}</div>
                      <div>Linhas processadas: {importResult.summary.processedRows}</div>
                      <div>Linhas com erro: {importResult.summary.errorRows}</div>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
            
            {/* Mostrar erros detalhados */}
            {importResult.summary?.errors && importResult.summary.errors.length > 0 && (
              <div className="mt-3">
                <h4 className="font-semibold text-sm mb-2">Erros encontrados:</h4>
                <div className="max-h-40 overflow-y-auto bg-gray-50 p-3 rounded text-sm">
                  {importResult.summary.errors.map((error, index) => (
                    <div key={index} className="mb-1">
                      <strong>Linha {error.row}:</strong> {error.error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Erro Geral */}
        {error && (
          <Alert className="border-red-200 bg-red-50">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
        )}

      </CardContent>
    </Card>
    </>
  );
};

export default ExcelImport;
